"use client";
import {
  ICompanySubMenu,
  MenuItemType,
} from "@/api/AppApi/services/company/interfaces/ICompany";
import { Link } from "@/app/components/lib/navigation/Link";

import { useLang } from "@/providers/langProvider";

const ALL_CATEGORIES_TITLES = ["All Categories", "كل التصنيفات"];

const MAX_ITEMS_PER_COLUMN = 8;
// Caps a section's width so it always fits the panel and wraps to a new row
// instead of overflowing; extra items make the columns taller (panel scrolls).
const MAX_SECTION_COLUMNS = 4;

const isAllCategoriesItem = (item: ICompanySubMenu): boolean => {
  return ALL_CATEGORIES_TITLES.includes(item.title);
};

interface Props {
  item: ICompanySubMenu;
  index: number;
}

const getLink = (item: ICompanySubMenu) => {
  const { type, value, title, slug } = item ?? {};
  const navigationSlug = slug ?? value;
  
  switch (type) {
    case MenuItemType.category:
      return value == "0" ? "/categories" : "/categories/" + navigationSlug;
    case MenuItemType.brand:
      return "/brands/" + navigationSlug;
    case MenuItemType.contact_us:
      return "/contact-us?title=" + title;
    case MenuItemType.inner_page:
      return "/inner-pages/" + value;
    case MenuItemType.blogs:
      return "/blog";
    default:
      return "#";
  }
};

// Check if any item in the array has sub_menu with items
const hasAnySubMenus = (items: ICompanySubMenu[]): boolean => {
  return items.some(item => item.sub_menu && item.sub_menu.length > 0);
};

// Recursively collect all items from nested structure and organize by top-level parents
const organizeMenuByTopLevelParents = (subMenu: ICompanySubMenu[]) => {
  // Filter out "All Categories" / "كل التصنيفات" items
  subMenu = subMenu.filter((item) => !isAllCategoriesItem(item));
  const sections: { parent: ICompanySubMenu; items: ICompanySubMenu[]; hasDeepNesting: boolean }[] = [];
  
  // Recursive function to collect all leaf items
  const collectAllItems = (items: ICompanySubMenu[]): ICompanySubMenu[] => {
    // Filter out "All Categories" items at each level
    items = items.filter((item) => !isAllCategoriesItem(item));
    const collected: ICompanySubMenu[] = [];

    items.forEach((item) => {
      if (item.sub_menu && item.sub_menu.length > 0) {
        // Has children - recursively collect them
        const childItems = collectAllItems(item.sub_menu);
        collected.push(...childItems);
      } else {
        // Leaf item - add it
        collected.push(item);
      }
    });
    
    return collected;
  };
  
  // Process each top-level item
  subMenu.forEach((topItem) => {
    if (topItem.sub_menu && topItem.sub_menu.length > 0) {
      // Has children - collect all nested items
      const allItems = collectAllItems(topItem.sub_menu);
      sections.push({
        parent: topItem,
        items: allItems,
        hasDeepNesting: true,
      });
    } else {
      // No children - standalone item
      sections.push({
        parent: topItem,
        items: [],
        hasDeepNesting: false,
      });
    }
  });
  
  return sections;
};

const MegaMenuItem: React.FC<Props> = ({ item, index }) => {
  const lang = useLang();
  const isRTL = lang === "ar";
  const hasSubMenu = item.sub_menu && item.sub_menu.length > 0;
  const itemId = `mega-nav-item-${index}`;
  const linkId = `mega-nav-link-${index}`;
  const menuId = `mega-menu-${index}`;

  // If no sub-menu, render simple link
  if (!hasSubMenu) {
    return (
      <div id={itemId} className="mega-nav-item">
        <Link id={linkId} href={getLink(item)} className="mega-nav-link">
          {item.title}
        </Link>
      </div>
    );
  }

  // Organize menu by top-level parents with all nested items flattened
  // Filter out "All Categories" / "كل التصنيفات" items
  const menuItems = (item.sub_menu || []).filter((item) => !isAllCategoriesItem(item));

  // Check if ANY of the menu items have sub_menus
  const hasSubMenus = hasAnySubMenus(menuItems);
  
  // If no sub-menus at all, show one flat list that flows into columns
  if (!hasSubMenus) {
    return (
      <div id={itemId} className="mega-nav-item">
        <div id={linkId} className="mega-nav-link">
          {item.title}
          <svg
            className="mega-nav-arrow"
            viewBox="0 0 10 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M1 1l4 4 4-4" />
          </svg>
        </div>
        
        <div id={menuId} className="mega-menu">
          <div
            id={`mega-inner-${index}`}
            className="mega-inner"
            style={{ direction: isRTL ? "rtl" : "ltr" }}
          >
            <div id={`mcol-${index}-0-0`} className="mega-flow">
              <Link
                id={`mcol-label-${index}-0-0`}
                href={getLink(item)}
                className="mcol-label"
                title={item.title}
              >
                {item.title}
              </Link>
              {/* Long flat lists (e.g. brands) flow into as many columns as fit, then scroll vertically */}
              <div
                className="mega-flow-list"
                style={
                  {
                    "--mega-flow-cols": Math.ceil(menuItems.length / MAX_ITEMS_PER_COLUMN),
                  } as React.CSSProperties
                }
              >
                {menuItems.map((childItem, childIndex) => (
                  <Link
                    key={childIndex}
                    id={`mcol-item-${index}-0-0-${childIndex}`}
                    href={getLink(childItem)}
                    title={childItem.title}
                  >
                    {childItem.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Has sub-menus - organize by sections and distribute across columns
  const sections = organizeMenuByTopLevelParents(menuItems);

  return (
    <div id={itemId} className="mega-nav-item">
      <div id={linkId} className="mega-nav-link">
        {item.title}
        <svg
          className="mega-nav-arrow"
          viewBox="0 0 10 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M1 1l4 4 4-4" />
        </svg>
      </div>
      
      <div id={menuId} className="mega-menu">
        <div
          id={`mega-inner-${index}`}
          className="mega-inner"
          style={{ direction: isRTL ? "rtl" : "ltr" }}
        >
          {sections.map((section, sectionIndex) => {
            const hasItems = section.items.length > 0;
            
            // If section has no items (no nesting), show as single column
            if (!hasItems) {
              return (
                <div key={sectionIndex} className="mega-section">
                  <div id={`mcol-${index}-${sectionIndex}-0`} className="mcol">
                    <Link
                      id={`mcol-label-${index}-${sectionIndex}-0`}
                      href={getLink(section.parent)}
                      className="mcol-label"
                      title={section.parent.title}
                    >
                      {section.parent.title}
                    </Link>
                  </div>
                </div>
              );
            }
            
            // If section has no deep nesting, show all items in ONE column
            if (!section.hasDeepNesting) {
              return (
                <div key={sectionIndex} className="mega-section">
                  <div id={`mcol-${index}-${sectionIndex}-0`} className="mcol">
                    <Link
                      id={`mcol-label-${index}-${sectionIndex}-0`}
                      href={getLink(section.parent)}
                      className="mcol-label"
                      title={section.parent.title}
                    >
                      {section.parent.title}
                    </Link>
                    {section.items.map((childItem, childIndex) => (
                      <Link
                        key={childIndex}
                        id={`mcol-item-${index}-${sectionIndex}-0-${childIndex}`}
                        href={getLink(childItem)}
                        title={childItem.title}
                      >
                        {childItem.title}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }
            
            // Section has deep nesting - distribute across columns
            const numColumns = Math.min(
              MAX_SECTION_COLUMNS,
              Math.max(1, Math.ceil(section.items.length / MAX_ITEMS_PER_COLUMN)),
            );
            const itemsPerColumn = Math.ceil(section.items.length / numColumns);
            
            return (
              <div key={sectionIndex} className="mega-section">
                {/* Render columns for this section */}
                {Array.from({ length: numColumns }).map((_, colIndex) => {
                  const startIdx = colIndex * itemsPerColumn;
                  const endIdx = Math.min(
                    startIdx + itemsPerColumn,
                    section.items.length
                  );
                  const columnItems = section.items.slice(startIdx, endIdx);
                  const colId = `mcol-${index}-${sectionIndex}-${colIndex}`;
                  const labelId = `mcol-label-${index}-${sectionIndex}-${colIndex}`;

                  return (
                    <div key={colIndex} id={colId} className="mcol">
                      {colIndex === 0 ? (
                        <Link
                          id={labelId}
                          href={getLink(section.parent)}
                          className="mcol-label"
                          title={section.parent.title}
                        >
                          {section.parent.title}
                        </Link>
                      ) : (
                        <span
                          id={`${labelId}-blank`}
                          className="mcol-label-blank"
                        />
                      )}
                      {columnItems.map((childItem, childIndex) => (
                        <Link
                          key={childIndex}
                          id={`mcol-item-${index}-${sectionIndex}-${colIndex}-${childIndex}`}
                          href={getLink(childItem)}
                          title={childItem.title}
                        >
                          {childItem.title}
                        </Link>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MegaMenuItem;
